import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

// Make sure these paths match wherever you store your SVGs:
import { ReactComponent as UploadIcon } from '../../resources/icons/upload.svg';
import { ReactComponent as XIcon } from '../../resources/icons/x.svg';
import downArrow from '../../resources/down_arrow.png'; // arrow for collapse/expand

interface MultiImageUploadProps {
  files: File[];
  setFiles: (files: File[]) => void;
  maxFiles?: number;

  // New props for upload status
  /**
   * Either "" | "uploading" | "complete" | "failed"
   */
  uploadStatus: '' | 'uploading' | 'complete' | 'failed';
  /**  Only used when uploadStatus === "failed" */
  uploadError?: string;
}

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  files,
  setFiles,
  maxFiles = 50,
  uploadStatus,
  uploadError,
}) => {
  // Track whether the preview grid is expanded
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  // Called when the user drops or selects files
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const combined = [...files, ...acceptedFiles];
      const newFiles = combined.slice(0, maxFiles);
      setFiles(newFiles);
    },
    [files, setFiles, maxFiles]
  );

  // Configure react-dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg'],
    },
    multiple: true,
    maxFiles: maxFiles,
  });

  // Remove a file by its index
  const handleRemove = (indexToRemove: number) => {
    const updated = files.filter((_, idx) => idx !== indexToRemove);
    setFiles(updated);
  };

  return (
    <div className="border border-gray-200 rounded p-4">
      {/* ───────────── Header / Static Instructions ───────────── */}
      <div className="flex flex-col mb-2 text-center gap-2">
        <h3 className="text-lg font-semibold">Add project image(s)</h3>
        <p className="text-sm text-gray-500">
          You may upload up to {maxFiles} images.
        </p>
        <p className="text-sm text-gray-500">SVG, PNG, JPG.</p>
      </div>

      {/* ───────────── Dropzone Area ───────────── */}
      <div
        {...getRootProps()}
        className="
          rounded cursor-pointer
          flex items-center justify-center 
          hover:border-blue-400 transition
        "
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-gray-500">Drop images here...</p>
        ) : (
          <div className="flex items-center space-x-2">
            <UploadIcon className="w-5 h-5 text-buttonBlue" />
            <span className="text-buttonBlue">Upload image(s)</span>
          </div>
        )}
      </div>

      {/* ───────────── Images (N) + Collapse/Expand Arrow ───────────── */}
      <div
        className="mt-2 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span className="text-sm text-gray-700">Images ({files.length})</span>
        <img
          src={downArrow}
          alt="Toggle"
          className={`w-4 h-4 transform transition ${
            isExpanded ? '' : 'rotate-180'
          }`}
        />
      </div>

      {/* ───────────── Status Messages (“Uploading…”, etc.) ───────────── */}
      <div className="mt-1">
        {uploadStatus === 'uploading' && (
          <p className="text-sm text-gray-500 mb-2">Uploading...</p>
        )}
        {uploadStatus === 'complete' && (
          <p className="text-sm text-green-600 mb-2">Upload complete!</p>
        )}
        {uploadStatus === 'failed' && (
          <div className="mb-2">
            <p className="text-sm text-red-600">Upload failed</p>
            {uploadError && (
              <p className="text-xs text-red-600">{uploadError}</p>
            )}
          </div>
        )}
      </div>

      {/* ───────────── Previews Grid (conditionally shown) ───────────── */}
      {files.length > 0 && isExpanded && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="relative border border-gray-200 rounded overflow-hidden"
            >
              {/* Always‐visible “×” removal button */}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="
                  absolute top-1 right-1 z-10 
                  bg-white rounded-full p-1 
                  focus:outline-none
                "
              >
                <XIcon className="w-4 h-4 text-red-600" />
              </button>

              {/* Thumbnail Image */}
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="w-full h-24 object-cover rounded"
              />

              {/* Filename (truncated) */}
              <p className="text-xs text-gray-600 mt-1 px-1 truncate">
                {file.name}
              </p>

              {/* File size in KB */}
              <p className="text-[10px] text-gray-500 px-1">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiImageUpload;
